import * as THREE from 'three';

/**
 * Shared WebGL probe and renderer factory for the three.js effects. three
 * dropped WebGL 1 in r163, so only a WebGL 2 context counts as support.
 *
 * Whether the browser has WebGL 2 can't change during a session, so the probe
 * runs once. Refusals that come later (after a GPU crash, or a context lost
 * while three sets up) surface in createWebGLRenderer instead.
 */
let supported;

// A context that's no longer needed is handed back straight away rather than
// left for GC: browsers keep only ~16 alive, dropping the oldest past that, and
// a gallery remounts on every visit.
function release(gl) {
  gl?.getExtension('WEBGL_lose_context')?.loseContext();
}

export function hasWebGL() {
  if (supported !== undefined) return supported;
  try {
    const gl = document.createElement('canvas').getContext('webgl2');
    supported = !!gl;
    release(gl);
  } catch {
    supported = false;
  }
  return supported;
}

/**
 * `new THREE.WebGLRenderer(parameters)`, or null when it won't start. A passing
 * probe doesn't guarantee one: after a GPU crash the browser can refuse new
 * contexts for the page, or the context can be lost while three sets up. The
 * caller shows its static content instead.
 *
 * @param {object} [parameters] WebGLRenderer parameters; a canvas is created
 *   when none is passed, so a failed attempt can always be cleaned up
 * @returns {THREE.WebGLRenderer | null}
 */
export function createWebGLRenderer(parameters = {}) {
  const canvas = parameters.canvas ?? document.createElement('canvas');
  // three can give up after it already got a context (only without the
  // requested attributes, or setup failed afterwards) and drops it unreleased.
  // Note what it gets on this canvas so exactly that one is released: asking
  // the canvas afterwards would create a context whenever three never got one.
  // This relies on three calling canvas.getContext() while it constructs
  // (WebGLRenderer.js:374 in r186); recheck it when upgrading three.
  const own = Object.getOwnPropertyDescriptor(canvas, 'getContext');
  const { getContext } = canvas;
  let context = null;
  try {
    // Defined rather than assigned: assigning throws when a script has made
    // the prototype's getContext read-only.
    Object.defineProperty(canvas, 'getContext', {
      configurable: true,
      writable: true,
      value: (...args) => {
        const gl = getContext.apply(canvas, args);
        if (gl) context = gl;
        return gl;
      },
    });
    return new THREE.WebGLRenderer({ ...parameters, canvas });
  } catch {
    release(context);
    return null;
  } finally {
    // Put back what was there: normally nothing, leaving the prototype's method.
    if (own) Object.defineProperty(canvas, 'getContext', own);
    else delete canvas.getContext;
  }
}
